import React, { useEffect, useState } from 'react';
import { Subject } from '../types';
// Direct import of the SubjectService
import { SubjectService } from '../services/subject.service';

interface SubjectPanelProps {
  gradeId: number | null;
  onSelectSubject: (subjectId: number) => void;
}

const SubjectPanel: React.FC<SubjectPanelProps> = ({ gradeId, onSelectSubject }) => {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSubjectId, setSelectedSubjectId] = useState<number | ''>('');
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [newSubjectName, setNewSubjectName] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    const fetchSubjects = async () => {
      if (!gradeId) return;
      
      try {
        setLoading(true);
        // Use SubjectService directly instead of ApiService
        const subjectsData = await SubjectService.getSubjectsByGradeId(gradeId);
        setSubjects(subjectsData);
        setSelectedSubjectId('');
        setError(null);
      } catch (err) {
        console.error('Error fetching subjects:', err);
        setError('Failed to fetch subjects. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchSubjects();
  }, [gradeId]);

  const handleSubjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const subjectId = parseInt(e.target.value, 10);
    setSelectedSubjectId(subjectId);
    onSelectSubject(subjectId);
  };

  const handleAddSubject = () => {
    setShowAddForm(true);
  };

  const handleSaveSubject = async () => {
    if (!newSubjectName.trim()) {
      alert('Please enter a subject name');
      return;
    }
    
    if (!gradeId) {
      alert('No grade selected');
      return;
    }
    
    try {
      setIsSubmitting(true);
      // Use SubjectService directly
      const newSubject = await SubjectService.createSubject({
        name: newSubjectName.trim(),
        grade_id: gradeId
      });
      
      // Update local state with the new subject
      setSubjects([...subjects, newSubject]);
      
      // Clear form and hide it
      setShowAddForm(false);
      setNewSubjectName('');
      
      // Show success message
      alert(`Subject "${newSubject.name}" was successfully created!`);
    } catch (err) {
      console.error('Error creating subject:', err);
      alert('Failed to create subject. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelAddSubject = () => {
    setShowAddForm(false);
    setNewSubjectName('');
  };

  if (!gradeId) {
    return null;
  }

  if (loading) {
    return <div className="section">Loading subjects...</div>;
  }

  return (
    <div className="section">
      <h2>Subjects</h2>
      {error && <div className="error">{error}</div>}
      
      <div className="selector">
        <label htmlFor="subject-select">Select Subject:</label>
        <select 
          id="subject-select" 
          value={selectedSubjectId} 
          onChange={handleSubjectChange}
        >
          <option value="" disabled>Choose a subject</option>
          {subjects.map(subject => (
            <option key={subject.id} value={subject.id}>
              {subject.name}
            </option>
          ))}
        </select>
      </div>
      
      {!showAddForm ? (
        <button 
          id="add-subject-btn" 
          className="add-btn"
          onClick={handleAddSubject}
        >
          Add Subject
        </button>
      ) : (
        <div id="add-subject-form">
          <input 
            type="text" 
            id="new-subject-name" 
            placeholder="Subject Name"
            value={newSubjectName}
            onChange={(e) => setNewSubjectName(e.target.value)}
            disabled={isSubmitting}
          />
          <button 
            id="save-subject-btn" 
            onClick={handleSaveSubject}
            disabled={isSubmitting}
            className="btn-spacing"
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </button>
          <button 
            id="cancel-subject-btn" 
            onClick={handleCancelAddSubject}
            disabled={isSubmitting}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default SubjectPanel;